import { useEffect, useRef, useState } from "react";
import FeatherIcon from "../../common/FeatherIcon";

const TabNameInput = ({ tab, onSave }) => {
  const [label, setLabel] = useState(tab.label || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!editing && !pending.current) setLabel(tab.label || "");
    // Only sync a name received from the server/parent. Excluding the local
    // editing transition keeps the just-saved optimistic name visible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab.label]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const save = async () => {
    const name = label.trim();
    if (!name) {
      setError("Enter a section name.");
      return;
    }
    if (pending.current) return;
    if (name === tab.label) {
      setLabel(name);
      setEditing(false);
      setError("");
      return;
    }
    pending.current = true;
    setSaving(true);
    setError("");
    try {
      await onSave(tab.type, name);
      setLabel(name);
      setEditing(false);
    } catch {
      setError("Unable to save. Press Enter or leave the field to retry.");
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };

  const cancel = () => {
    setLabel(tab.label || "");
    setError("");
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="master-guide-tab-name master-guide-tab-name-display">
        <span className="master-guide-list-label">{label}</span>
        <button
          type="button"
          className="uk-button hms-simple-icon-btn master-guide-tab-edit-name"
          aria-label={`Edit ${label} name`}
          title="Edit name"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setEditing(true);
          }}
        >
          <FeatherIcon name="edit-3" />
        </button>
      </div>
    );
  }

  return (
    <div className="master-guide-tab-name">
      <input
        ref={inputRef}
        className="uk-input"
        type="text"
        aria-label={`Section name: ${tab.label}`}
        aria-invalid={!!error}
        aria-describedby={error ? `tab-name-error-${tab.type}` : undefined}
        value={label}
        disabled={saving}
        onClick={(event) => event.stopPropagation()}
        onChange={event => { setLabel(event.target.value); setError(""); }}
        onBlur={save}
        onKeyDown={event => {
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          } else if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            cancel();
          }
        }}
      />
      {error && <span id={`tab-name-error-${tab.type}`} role="alert" className="uk-text-danger uk-text-small">{error}</span>}
    </div>
  );
};

export default TabNameInput;
